import moment from 'moment';
import { formDateFormat } from '../../../../../constants';

/**
 * Phase 3.1: Extracted utility functions from TransportDialog.js
 * These functions handle form data preparation, payload building, and calculations
 */

/**
 * Get a property by ID from an array of objects
 * @param {number} id - The ID to search for
 * @param {Array} array - The array to search in
 * @param {string} property - The property to return
 * @returns {*} The property value or undefined
 */
export const getPropertyById = (id, array, property) => {
  const item = array?.find(item =>
    item.contactId === id || item.orgId === id || item.sourceId === id || item.outcomeId === id
  );
  return item?.[property];
};

/**
 * Get phone number for a contact ID
 * @param {number} contactId - The contact ID
 * @param {Array} contacts - Array of contacts
 * @returns {string} Phone number or empty string
 */
export const getPhoneNumber = (contactId, contacts) => {
  return getPropertyById(contactId, contacts, 'phone') || '';
};

/**
 * Get fax number for a contact ID
 * @param {number} contactId - The contact ID
 * @param {Array} contacts - Array of contacts
 * @returns {string} Fax number or empty string
 */
export const getFaxNumber = (contactId, contacts) => {
  return getPropertyById(contactId, contacts, 'fax') || '';
};

/**
 * Format a date value for the form
 * @param {string|Date} dateValue - The date to format
 * @returns {string} Formatted date or empty string
 */
export const formatFormDate = (dateValue) => {
  if (!dateValue) return '';
  const formatted = moment(dateValue).format(formDateFormat);
  return formatted === 'Invalid date' ? '' : formatted;
};

/**
 * Prepare default values for react-hook-form from Redux transport form state
 * @param {Object} transportForm - The Redux transport form state
 * @param {Array} contacts - Array of contacts for phone/fax lookup
 * @returns {Object} Default values object for react-hook-form
 */
export const prepareFormDefaults = (transportForm, contacts = []) => {
  const {
    trnID,
    fundingSTOPBilltoOrganization,
    departingSAPorJailDepartureLocation,
    communityProviderFacility,
    organizationPerformingPickUpInitial,
    fundingSTOPContact,
    SAPTransitionalCounselor,
    personPerformingPickInitialDeparture,
    personReceivingDestination,
    source,
    highControl,
    specialTransport,
    planIncludesFlight,
    SASCARepresentativeTransportationCoordinator,
    transportCompletedByOrganization,
    initialDepartureDate,
    dateSAPConfirmedRelease,
    SAPStaffConfirmingRelease,
    actualDepartureDate,
    outcome,
    whoContactedConfirmationCallmadeto,
    contactedByConfirmationCallmadeby,
    callDate,
    callTime,
    staffTime1By4hrs,
    mileage,
    estimatedICDTPFlatFee,
    StaffTimeQty,
    staffTimeOverTime,
    staffTimeRate,
    staffTimeAmount,
    mileageQty,
    mileageRate,
    mileageAmount,
    ICDTPFlatFee,
    totalCombinedActualCosts,
    comments,
    Void,
    invoiceID,
  } = transportForm;

  return {
    trnID: trnID || '',
    fundingSTOPBilltoOrganization: fundingSTOPBilltoOrganization || '',
    departingSAPorJailDepartureLocation: departingSAPorJailDepartureLocation || '',
    communityProviderFacility: communityProviderFacility || '',
    organizationPerformingPickUpInitial: organizationPerformingPickUpInitial || '',
    fundingSTOPContact: fundingSTOPContact || '',
    fundingSTOPContactPhone: getPhoneNumber(fundingSTOPContact, contacts),
    fundingSTOPContactFax: getFaxNumber(fundingSTOPContact, contacts),
    SAPTransitionalCounselor: SAPTransitionalCounselor || '',
    SAPTransitionalCounselorPhone: getPhoneNumber(SAPTransitionalCounselor, contacts),
    SAPTransitionalCounselorFax: getFaxNumber(SAPTransitionalCounselor, contacts),
    personPerformingPickInitialDeparture: personPerformingPickInitialDeparture || '',
    personPerformingPickInitialDeparturePhone: getPhoneNumber(personPerformingPickInitialDeparture, contacts),
    personPerformingPickInitialDepartureFax: getFaxNumber(personPerformingPickInitialDeparture, contacts),
    personReceivingDestination: personReceivingDestination || '',
    personReceivingDestinationPhone: getPhoneNumber(personReceivingDestination, contacts),
    personReceivingDestinationFax: getFaxNumber(personReceivingDestination, contacts),
    source: source || '',
    highControl: highControl === null ? '' : highControl,
    specialTransport: specialTransport === null ? '' : specialTransport,
    planIncludesFlight: planIncludesFlight === null ? '' : planIncludesFlight,
    SASCARepresentativeTransportationCoordinator: SASCARepresentativeTransportationCoordinator || '',
    transportCompletedByOrganization: transportCompletedByOrganization || '',
    initialDepartureDate: formatFormDate(initialDepartureDate),
    dateSAPConfirmedRelease: formatFormDate(dateSAPConfirmedRelease),
    SAPStaffConfirmingRelease: SAPStaffConfirmingRelease || '',
    SAPStaffConfirmingReleasePhone: getPhoneNumber(SAPStaffConfirmingRelease, contacts),
    actualDepartureDate: formatFormDate(actualDepartureDate),
    outcome: outcome || '',
    whoContactedConfirmationCallmadeto: whoContactedConfirmationCallmadeto || '',
    contactedByConfirmationCallmadeby: contactedByConfirmationCallmadeby || '',
    callDate: formatFormDate(callDate),
    callTime: callTime || '',
    staffTime1By4hrs: staffTime1By4hrs,
    mileage: mileage,
    estimatedICDTPFlatFee: estimatedICDTPFlatFee,
    StaffTimeQty: StaffTimeQty,
    staffTimeOverTime: staffTimeOverTime,
    staffTimeRate: staffTimeRate,
    staffTimeAmount: staffTimeAmount,
    mileageQty: mileageQty,
    mileageRate: mileageRate,
    mileageAmount: mileageAmount,
    ICDTPFlatFee: ICDTPFlatFee,
    totalCombinedActualCosts: totalCombinedActualCosts,
    comments: comments || '',
    Void: Void,
    invoiceID: invoiceID,
  };
};

/**
 * Format a date value for API submission
 * @param {string|Date} dateValue - The date to format
 * @returns {string|null} Formatted date or null if invalid
 */
export const formatApiDate = (dateValue) => {
  if (!dateValue) return null;
  const formatted = moment(dateValue).format(formDateFormat);
  return formatted === 'Invalid date' ? null : formatted;
};

/**
 * Build API payload from form data and transport form state
 * @param {Object} formData - Form data from react-hook-form (not used in current implementation)
 * @param {Object} transportForm - Redux transport form state
 * @param {number} episodeId - Episode ID
 * @param {boolean} isNewForm - Whether this is a new transport form
 * @returns {Object} Payload object for API submission
 */
export const buildTransportPayload = (formData, transportForm, episodeId, isNewForm) => {
  const {
    trnID,
    fundingSTOPBilltoOrganization,
    departingSAPorJailDepartureLocation,
    communityProviderFacility,
    organizationPerformingPickUpInitial,
    fundingSTOPContact,
    SAPTransitionalCounselor,
    personPerformingPickInitialDeparture,
    personReceivingDestination,
    source,
    highControl,
    specialTransport,
    planIncludesFlight,
    SASCARepresentativeTransportationCoordinator,
    transportCompletedByOrganization,
    initialDepartureDate,
    dateSAPConfirmedRelease,
    SAPStaffConfirmingRelease,
    actualDepartureDate,
    outcome,
    whoContactedConfirmationCallmadeto,
    contactedByConfirmationCallmadeby,
    callDate,
    callTime,
    staffTime1By4hrs,
    mileage,
    estimatedICDTPFlatFee,
    StaffTimeQty,
    staffTimeOverTime,
    staffTimeRate,
    staffTimeAmount,
    mileageQty,
    mileageRate,
    mileageAmount,
    ICDTPFlatFee,
    totalCombinedActualCosts,
    comments,
    Void,
    invoiceID,
  } = transportForm;

  return {
    TrnEpdId: episodeId,
    trnID: isNewForm ? 0 : trnID,
    fundingSTOPBilltoOrganization: fundingSTOPBilltoOrganization || null,
    departingSAPorJailDepartureLocation: departingSAPorJailDepartureLocation || null,
    communityProviderFacility: communityProviderFacility || null,
    organizationPerformingPickUpInitial: organizationPerformingPickUpInitial || null,
    fundingSTOPContact: fundingSTOPContact || null,
    SAPTransitionalCounselor: SAPTransitionalCounselor || null,
    personPerformingPickInitialDeparture: personPerformingPickInitialDeparture || null,
    personReceivingDestination: personReceivingDestination || null,
    source: source || null,
    highControl: highControl === '' ? null : highControl,
    specialTransport: specialTransport === '' ? null : specialTransport,
    planIncludesFlight: planIncludesFlight === '' ? null : planIncludesFlight,
    SASCARepresentativeTransportationCoordinator: SASCARepresentativeTransportationCoordinator || null,
    transportCompletedByOrganization: transportCompletedByOrganization || null,
    initialDepartureDate: formatApiDate(initialDepartureDate),
    dateSAPConfirmedRelease: formatApiDate(dateSAPConfirmedRelease),
    SAPStaffConfirmingRelease: SAPStaffConfirmingRelease || null,
    actualDepartureDate: formatApiDate(actualDepartureDate),
    outcome: outcome || null,
    whoContactedConfirmationCallmadeto: whoContactedConfirmationCallmadeto || null,
    contactedByConfirmationCallmadeby: contactedByConfirmationCallmadeby || null,
    callDate: formatApiDate(callDate),
    callTime: callTime || '',
    staffTime1By4hrs: staffTime1By4hrs,
    mileage: mileage,
    estimatedICDTPFlatFee: estimatedICDTPFlatFee,
    StaffTimeQty: StaffTimeQty,
    staffTimeOverTime: staffTimeOverTime,
    staffTimeRate: staffTimeRate,
    staffTimeAmount: staffTimeAmount,
    mileageQty: mileageQty,
    mileageRate: mileageRate,
    mileageAmount: mileageAmount,
    ICDTPFlatFee: ICDTPFlatFee,
    totalCombinedActualCosts: totalCombinedActualCosts,
    comments: comments || '',
    Void: Void,
    invoiceID: invoiceID,
  };
};

/**
 * Calculate billing amount
 * @param {number} qty - Quantity
 * @param {number} rate - Rate
 * @param {number} overtime - Overtime hours
 * @returns {number} Calculated amount
 */
export const calculateBillingAmount = (qty, rate, overtime = 0) => {
  const quantity = Number(qty) || 0;
  const rateValue = Number(rate) || 0;
  const overtimeValue = Number(overtime) || 0;

  return (quantity + overtimeValue) * rateValue;
};

/**
 * Round a numeric value to nearest integer
 * @param {number} value - Value to round
 * @returns {number} Rounded value
 */
export const roundValue = (value) => {
  return Math.round(Number(value) || 0);
};
